// src/layouts/MainLayout/MainLayout.jsx
import React from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "hooks/useUser";
import { logout } from "services/auth";
import { Button } from "components/ui/button";
import { ThemeToggle } from "components/ThemeToggle";
import logoEmpresa from "assets/images/Arvore timbo transparente colorida.png";
import { Menu, Home, PieChart, Package, Receipt, RefreshCw } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTrigger,
} from "components/ui/sheet";

function MainLayout({ children }) {
  const { user } = useAuth();
  const { isAdmin, canViewDashboard, canViewEstoque, canViewGuias } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      alert("Ocorreu um erro ao sair. Tente novamente.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-background">
      <header className="sticky top-0 z-40 w-full border-b border-green-900 dark:border-green-900 bg-green-900 dark:bg-card text-white">
        <div className="container flex h-16 items-center justify-between relative">
          <Link
            to="/"
            className="flex items-center space-x-3 rtl:space-x-reverse w-full justify-center md:w-auto md:justify-start"
          >
            <img
              src={logoEmpresa}
              alt="Logo da Empresa"
              className="h-8 w-auto"
            />
            <span className="self-center text-xl font-bold whitespace-nowrap text-white">
              Gestão Timbó
            </span>
          </Link>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-green-800 hover:text-white"
            onClick={() => window.location.reload()}
            title="Atualizar Aplicativo"
          >
            <RefreshCw className="h-5 w-5" />
          </Button>

          <nav className="hidden md:flex font-medium items-center space-x-2">
            {canViewDashboard && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/resumo">Resumo</Link>
              </Button>
            )}
            {isAdmin && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/clientes">Clientes</Link>
              </Button>
            )}
            {isAdmin && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/creditos">Créditos</Link>
              </Button>
            )}
            {canViewEstoque && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/estoque">Estoque</Link>
              </Button>
            )}
            {canViewGuias && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/guias">Guias</Link>
              </Button>
            )}
            {isAdmin && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/fiscal">Fiscal</Link>
              </Button>
            )}
            {isAdmin && (
              <Button
                asChild
                variant="ghost"
                className="text-white hover:bg-green-800 hover:text-white"
              >
                <Link to="/admin">Admin</Link>
              </Button>
            )}
            <div className="text-sm text-right">
              <span className="text-green-100 dark:text-muted-foreground">
                Olá,
              </span>
              <span className="font-semibold ml-1">
                {user?.displayName || user?.email}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.location.reload()}
              className="text-white hover:bg-green-800 hover:text-white"
              title="Atualizar Aplicativo"
            >
              <RefreshCw className="h-5 w-5" />
            </Button>
            <ThemeToggle className="text-white hover:bg-green-800 hover:text-white" />
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="bg-white text-green-900 border-0 hover:bg-green-50 hover:text-green-900 dark:bg-white dark:text-green-900 dark:hover:bg-green-50"
            >
              Sair
            </Button>
          </nav>
        </div>
      </header>

      <main className="flex-grow p-3 md:p-6 w-full max-w-full overflow-x-hidden pb-20 md:pb-6">
        {children}
      </main>

      {/* Bottom Navigation Bar (Mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-card border-t border-gray-200 dark:border-border flex justify-around items-center h-16 pb-safe">
        <Link
          to="/"
          className={`flex flex-col items-center justify-center w-full h-full transition-colors ${isActive("/") ? "text-green-700 dark:text-green-500" : "text-slate-500 hover:text-green-600 dark:hover:text-green-400"}`}
        >
          <Home className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium">Início</span>
        </Link>

        {canViewDashboard && (
          <Link
            to="/resumo"
            className={`flex flex-col items-center justify-center w-full h-full transition-colors ${isActive("/resumo") ? "text-green-700 dark:text-green-500" : "text-slate-500 hover:text-green-600 dark:hover:text-green-400"}`}
          >
            <PieChart className="h-5 w-5 mb-1" />
            <span className="text-[10px] font-medium">Resumo</span>
          </Link>
        )}

        {canViewEstoque && (
          <Link
            to="/estoque"
            className={`flex flex-col items-center justify-center w-full h-full transition-colors ${isActive("/estoque") ? "text-green-700 dark:text-green-500" : "text-slate-500 hover:text-green-600 dark:hover:text-green-400"}`}
          >
            <Package className="h-5 w-5 mb-1" />
            <span className="text-[10px] font-medium">Estoque</span>
          </Link>
        )}

        {isAdmin && (
          <Link
            to="/fiscal"
            className={`flex flex-col items-center justify-center w-full h-full transition-colors ${isActive("/fiscal") ? "text-green-700 dark:text-green-500" : "text-slate-500 hover:text-green-600 dark:hover:text-green-400"}`}
          >
            <Receipt className="h-5 w-5 mb-1" />
            <span className="text-[10px] font-medium">Fiscal</span>
          </Link>
        )}

        {/* Menu "Mais" que abre o Sheet */}
        <Sheet>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center justify-center w-full h-full text-slate-500 hover:text-green-600 dark:hover:text-green-400 focus:outline-none">
              <Menu className="h-5 w-5 mb-1" />
              <span className="text-[10px] font-medium">Mais</span>
            </button>
          </SheetTrigger>
          <SheetContent side="left">
            <div className="flex flex-col h-full">
              <div className="font-bold text-lg border-b pb-2 mb-2 text-green-800 dark:text-muted-foreground">
                Mais Opções
              </div>
              <nav className="flex flex-col space-y-4 text-lg mt-4">
                <SheetClose asChild>
                  <Link to="/">Página Inicial</Link>
                </SheetClose>
                {canViewDashboard && (
                  <SheetClose asChild>
                    <Link to="/resumo">Resumo</Link>
                  </SheetClose>
                )}
                {isAdmin && (
                  <SheetClose asChild>
                    <Link to="/clientes">Clientes</Link>
                  </SheetClose>
                )}
                {isAdmin && (
                  <SheetClose asChild>
                    <Link to="/creditos">Créditos</Link>
                  </SheetClose>
                )}
                {canViewEstoque && (
                  <SheetClose asChild>
                    <Link to="/estoque">Estoque</Link>
                  </SheetClose>
                )}
                {canViewGuias && (
                  <SheetClose asChild>
                    <Link to="/guias">Guias</Link>
                  </SheetClose>
                )}
                {isAdmin && (
                  <SheetClose asChild>
                    <Link to="/fiscal">Fiscal</Link>
                  </SheetClose>
                )}
                {isAdmin && (
                  <SheetClose asChild>
                    <Link to="/admin">Admin</Link>
                  </SheetClose>
                )}
              </nav>

              <div className="mt-auto space-y-4">
                <div className="text-sm text-left border-t pt-4">
                  <span className="font-semibold text-slate-700 dark:text-muted-foreground">
                    {user?.displayName || user?.email}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <ThemeToggle />
                  <SheetClose asChild>
                    <Button onClick={handleLogout} variant="outline" size="sm">
                      Sair
                    </Button>
                  </SheetClose>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}

export default MainLayout;
